import React, { useState, useEffect, useRef, useMemo } from "react"

// Custom Dropdown Component for LawyerProfilePage
const LawyerPageDropdown = React.memo(({ options, label, value, onChange, className, placeholder = "Select...", isClearable = false }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchValue, setSearchValue] = useState("")
  const dropdownRef = useRef(null)
  const searchInputRef = useRef(null)
  
  // Use useMemo to calculate filtered options instead of state
  const filteredOptions = useMemo(() => {
    if (!searchValue.trim()) return options || [];
    return (options || []).filter(option => 
      option.label.toLowerCase().includes(searchValue.toLowerCase())
    );
  }, [options, searchValue]);

  const toggleDropdown = () => {
    setIsOpen(!isOpen)
    if (!isOpen) {
      // Reset search when opening dropdown
      setSearchValue("")
      // Focus the search input after dropdown opens
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus()
        }
      }, 50)
    }
  }

  const handleSelect = (option) => {
    console.log("Selected option:", option);
    onChange(option);
    setIsOpen(false);
    setSearchValue("");
  }

  const handleClear = (e) => {
    e.stopPropagation(); // Prevent dropdown from opening
    e.preventDefault(); // Ensure no default action occurs
    console.log("Clearing selection");
    
    // Add a small delay to ensure the event doesn't conflict
    setTimeout(() => {
      // Call onChange with null to clear the selection
      onChange(null);
      
      // Close the dropdown when clearing
      setIsOpen(false);
    }, 10);
  }

  const handleSearchChange = (e) => {
    const value = e.target.value
    setSearchValue(value)
  }

  // Click outside handler
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
        setSearchValue("")
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside)
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [isOpen])

  return (
    <div className={`lawyer-profile-page__custom-dropdown ${className || ""}`} ref={dropdownRef}>
      <div className={`lawyer-profile-page__dropdown-selected ${isOpen ? "expanded" : ""}`} onClick={toggleDropdown}>
        <div className="lawyer-profile-page__dropdown-content">
          <span className="lawyer-profile-page__dropdown-label">{label}</span>
          <span className="lawyer-profile-page__dropdown-value">{value?.label || placeholder}</span>
        </div>
        
        <div className="lawyer-profile-page__dropdown-icons">
          {/* Show clear button if we have a value and isClearable is true */}
          {isClearable && value && (
            <button 
              type="button"
              className="lawyer-profile-page__dropdown-clear-btn"
              onClick={handleClear}
              aria-label="Clear selection"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          )}
          
          <div className="lawyer-profile-page__dropdown-arrow">
            <svg width="14" height="10" viewBox="0 0 330 330" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15,180h263.787l-49.394,49.394c-5.858,5.857-5.858,15.355,0,21.213C232.322,253.535,236.161,255,240,255 s7.678-1.465,10.606-4.394l75-75c5.858-5.857,5.858-15.355,0-21.213l-75-75c-5.857-5.857-15.355-5.857-21.213,0 c-5.858,5.857-5.858,15.355,0,21.213L278.787,150H15c-8.284,0-15,6.716-15,15S6.716,180,15,180z" fill="black"/>
            </svg>
          </div>
        </div>
      </div>

      {isOpen && (
        <ul className="lawyer-profile-page__dropdown-options">
          {/* Search input at the top of options */}
          <li className="lawyer-profile-page__dropdown-search">
            <input
              ref={searchInputRef}
              type="text"
              value={searchValue}
              onChange={handleSearchChange}
              placeholder="Search..."
              onClick={(e) => e.stopPropagation()}
              className="lawyer-profile-page__dropdown-search-input"
            />
          </li>
          
          {/* Display filtered options */}
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option, index) => (
              <li
                key={index}
                onClick={() => handleSelect(option)}
                className={value && value.value === option.value ? "selected" : ""}
              >
                {option.label}
              </li>
            ))
          ) : (
            <li className="lawyer-profile-page__dropdown-no-results">No options match your search</li>
          )}
        </ul>
      )}
    </div>
  )
})

export default LawyerPageDropdown 